import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { DayHeader } from './Schedule';
import PARTY from '../data/partyInfo';

// Hidden page for the wedding party (reached by link only, like /admin):
// their timeline, who's who, attire, FAQs and contacts in one place.
// All copy lives in src/data/partyInfo.js.

const card = {
  background: 'white',
  borderRadius: 'var(--radius-lg)',
  overflow: 'hidden',
  boxShadow: 'var(--shadow-lg)',
  border: '1px solid rgba(45, 80, 22, 0.1)',
  marginBottom: '32px',
};
const sectionTitle = {
  fontFamily: 'var(--font-serif)',
  fontSize: '1.9rem',
  color: 'var(--green-dark)',
  margin: '0 0 6px',
  lineHeight: 1.15,
};
const sectionSub = {
  fontFamily: 'var(--font-sans)',
  fontSize: '0.82rem',
  color: 'var(--text-light)',
  margin: '0 0 22px',
  fontWeight: 300,
};
const roleLabel = {
  fontFamily: 'var(--font-sans)',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--green)',
  marginBottom: '6px',
};
const nameList = {
  fontFamily: 'var(--font-serif)',
  fontSize: '1.08rem',
  color: 'var(--text-dark)',
  lineHeight: 1.5,
  margin: '0 0 18px',
};

function RoleBlock({ group }) {
  return (
    <div>
      <div style={roleLabel}>{group.role}</div>
      <div style={nameList}>{group.names.map(n => <div key={n}>{n}</div>)}</div>
    </div>
  );
}

export default function WeddingParty() {
  const [openItems, setOpenItems] = useState({});
  const toggle = key => setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  const { intro, keyMoments, days, roster, attire, faqs, contacts } = PARTY;

  return (
    <div className="site-wrapper">
      <div className="rsvp-bg-photo" style={{ backgroundImage: "url('/Saint William.png')" }} />
      <div className="rsvp-bg-overlay" />

      <div className="page-content" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '40px 24px 80px' }}>

          {/* ── Title ─────────────────────────────────────────────────── */}
          <h2 style={{ fontFamily: 'var(--font-script)', fontSize: '60px', fontWeight: 400, color: 'var(--green-dark)',
            textAlign: 'center', marginBottom: '8px', lineHeight: 1.2 }}>{intro.title}</h2>
          <p style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 600, fontSize: '1rem', color: 'grey', fontFamily: 'var(--font-sans)' }}>
            {intro.tagline}
          </p>
          <p style={{ textAlign: 'center', margin: '0 auto 28px', maxWidth: '560px', fontSize: '0.8rem', color: 'var(--text-light)',
            fontFamily: 'var(--font-sans)', fontStyle: 'italic', lineHeight: 1.6 }}>
            {intro.note}
          </p>

          {/* ── Key moments ───────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '32px' }}>
            {keyMoments.map(m => (
              <div key={m.title} style={{ ...card, marginBottom: 0, padding: '18px 20px' }}>
                <div style={{ ...roleLabel, color: 'var(--text-light)' }}>{m.day}</div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', color: 'var(--green-dark)', lineHeight: 1.2 }}>{m.title}</div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '6px 0 2px' }}>{m.time}</div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.8rem', color: 'var(--text-medium)' }}>{m.place}</div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.74rem', color: 'var(--text-light)', fontStyle: 'italic', marginTop: '8px', lineHeight: 1.5 }}>{m.note}</div>
              </div>
            ))}
          </div>

          {/* ── Timeline (same layout as the public Schedule) ─────────── */}
          <div style={card}>
            {days.map(day => (
              <React.Fragment key={day.num}>
                <DayHeader day={day} />
                <div className="schedule-timeline" style={{ margin: 0, maxWidth: 'none', padding: '36px 40px' }}>
                  <div className="schedule-day" style={{ marginBottom: 0 }}>
                    <div className="schedule-events">
                      {day.events.map(event => (
                        <div className={`schedule-event${event.subtle ? ' schedule-event--subtle' : ''}`} key={event.name}>
                          <div className="schedule-time">
                            {event.time && <>{event.time}</>}
                            {event.time && event.end && <><br /><span>– {event.end}</span></>}
                          </div>
                          <div className="schedule-event-body">
                            <div className="schedule-event-name" style={event.subtle ? { fontSize: '0.85rem', fontFamily: 'var(--font-sans)', fontWeight: 500, color: 'var(--text-light)', letterSpacing: '0.06em', textTransform: 'uppercase' } : undefined}>
                              {event.name}
                            </div>
                            {event.venue && (
                              <div className="schedule-event-detail">
                                <span>📍</span>
                                <span><a href={event.venueLink} target="_blank" rel="noopener noreferrer">{event.venue}</a></span>
                              </div>
                            )}
                            {event.address && (
                              <div className="schedule-event-detail">
                                <span style={{ opacity: 0 }}>📍</span>
                                <span>{event.address}</span>
                              </div>
                            )}
                            {event.notes && (
                              <div className="schedule-event-detail" style={{ marginTop: '8px' }}>
                                <span style={{ opacity: 0 }}>📍</span>
                                <span style={{ fontStyle: 'italic', color: 'var(--text-light)' }}>{event.notes}</span>
                              </div>
                            )}
                            {event.bulletNotes && (
                              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                                {event.bulletNotes.map((note, i) => (
                                  <li key={i} style={{ fontStyle: 'italic', color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '6px', fontFamily: 'var(--font-sans)' }}>{note}</li>
                                ))}
                              </ul>
                            )}
                            {event.attire && <div className="schedule-attire-badge">Attire: {event.attire}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* ── Who's who ─────────────────────────────────────────────── */}
          <div style={{ ...card, padding: '32px 40px' }}>
            <h3 style={sectionTitle}>Who's Who</h3>
            <p style={sectionSub}>The crew standing up with us, and everyone with a part in the Mass.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 32px' }}>
              <div>
                <div style={{ ...sectionTitle, fontSize: '1.3rem', marginBottom: '14px' }}>Caroline's side</div>
                {roster.bride.map(g => <RoleBlock key={g.role} group={g} />)}
              </div>
              <div>
                <div style={{ ...sectionTitle, fontSize: '1.3rem', marginBottom: '14px' }}>Michael's side</div>
                {roster.groom.map(g => <RoleBlock key={g.role} group={g} />)}
              </div>
            </div>

            <div style={{ borderTop: '1px solid rgba(45, 80, 22, 0.1)', marginTop: '10px', paddingTop: '24px' }}>
              <div style={{ ...sectionTitle, fontSize: '1.3rem', marginBottom: '14px' }}>In the Mass</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 32px' }}>
                {roster.ceremony.map(g => <RoleBlock key={g.role} group={g} />)}
              </div>
            </div>

            <div style={{ borderTop: '1px solid rgba(45, 80, 22, 0.1)', marginTop: '10px', paddingTop: '24px',
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 32px' }}>
              <div>
                <div style={roleLabel}>Walking in the family</div>
                <div style={nameList}>{roster.escorts.map(e => <div key={e}>{e}</div>)}</div>
              </div>
              <div>
                <div style={roleLabel}>Processional order</div>
                <ol style={{ ...nameList, paddingLeft: '22px' }}>
                  {roster.processional.map(pair => <li key={pair.join()}>{pair.join(' · ')}</li>)}
                </ol>
                <p style={{ ...sectionSub, margin: 0 }}>Brian and Julie lead, then each groomsman walks with two bridesmaids.</p>
              </div>
            </div>
          </div>

          {/* ── Attire ────────────────────────────────────────────────── */}
          <div style={{ ...card, padding: '32px 40px' }}>
            <h3 style={sectionTitle}>What to Wear</h3>
            <p style={sectionSub}>Two very different nights — pack for both.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px 32px' }}>
              {attire.map(a => (
                <div key={a.label}>
                  <div style={roleLabel}>{a.label}</div>
                  <p style={{ fontFamily: 'var(--font-sans)', fontSize: '0.85rem', color: 'var(--text-medium)', lineHeight: 1.6, margin: 0, fontWeight: 300 }}>{a.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── FAQs (same accordion as the public FAQ page) ─────────── */}
          <div style={{ ...card, padding: '32px 40px' }}>
            <h3 style={sectionTitle}>Wedding Party FAQs</h3>
            <p style={sectionSub}>The questions we've been asked most. General weekend questions are on the <Link to="/faq" style={{ color: 'var(--green)', textDecoration: 'underline' }}>FAQ page</Link>.</p>
            <div className="faq-list">
              {faqs.map(item => {
                const isOpen = !!openItems[item.q];
                return (
                  <div className={`faq-item ${isOpen ? 'open' : ''}`} key={item.q}>
                    <button className="faq-question" onClick={() => toggle(item.q)}>
                      <span className="faq-question-text">{item.q}</span>
                      <span className="faq-icon">+</span>
                    </button>
                    <div className="faq-answer">
                      <p dangerouslySetInnerHTML={{ __html: item.a }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Contacts + links ──────────────────────────────────────── */}
          <div className="card-sm" style={{ background: 'var(--green-pale)', border: '1px solid rgba(61,107,53,0.15)', padding: '28px 32px' }}>
            <div style={{ ...sectionTitle, fontSize: '1.4rem', marginBottom: '14px' }}>Contacts</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px 24px', marginBottom: '22px' }}>
              {contacts.map(c => (
                <div key={c.name}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', color: 'var(--text-dark)' }}>{c.name}</div>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.76rem', color: 'var(--text-medium)', fontWeight: 300, lineHeight: 1.5 }}>{c.role}</div>
                  {c.email && <a href={`mailto:${c.email}`} style={{ fontFamily: 'var(--font-sans)', fontSize: '0.76rem', color: 'var(--green)', textDecoration: 'underline' }}>{c.email}</a>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <Link to="/schedule" className="btn btn-gold">Full Weekend Schedule</Link>
              <Link to="/travel" className="btn btn-outline">Travel &amp; Stay</Link>
              <Link to="/faq" className="btn btn-outline">FAQ</Link>
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
